// Workflow `definition` JSONB is read by the executor as raw snake_case
// (workflow_executor.py:88-93 reads `action_def.get("action_type")` etc.,
// and the action handlers read `action_config.get("entity_type")`,
// `action_config.get("task_template_id")`, `data.get("listing_id")`, etc.),
// but the UI editor / TS types use camelCase. Pydantic's camelCase alias
// only applies to top-level request fields — `definition` is Dict[str, Any]
// and pass-through, so the keys we send are the keys persisted. Without
// this layer, every workflow saved via the editor crashes at run time:
// either `action_type=None` → action_log NOT NULL violation (HTTP 500),
// or the action handler silently fails because `entity_type` / `data`
// column names don't match.

function camelToSnake(s: string): string {
  return s.replace(/([A-Z])/g, '_$1').toLowerCase()
}

function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c) => (c as string).toUpperCase())
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return (
    !!v &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    Object.getPrototypeOf(v) === Object.prototype
  )
}

function deepRekeyCamelToSnake(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(deepRekeyCamelToSnake)
  if (isPlainObject(v)) {
    const out: Record<string, unknown> = {}
    for (const [k, val] of Object.entries(v)) {
      out[camelToSnake(k)] = deepRekeyCamelToSnake(val)
    }
    return out
  }
  return v
}

function deepRekeySnakeToCamel(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(deepRekeySnakeToCamel)
  if (isPlainObject(v)) {
    const out: Record<string, unknown> = {}
    for (const [k, val] of Object.entries(v)) {
      out[snakeToCamel(k)] = deepRekeySnakeToCamel(val)
    }
    return out
  }
  return v
}

interface BackendAction {
  name?: string
  action_type?: string
  action_config?: Record<string, unknown>
  condition?: unknown
  on_failure?: string
}

interface FrontendAction {
  name?: string
  actionType?: string
  actionConfig?: Record<string, unknown>
  condition?: unknown
  onFailure?: string
}

function actionToBackend(a: FrontendAction): BackendAction {
  return {
    name: a.name,
    action_type: a.actionType,
    // action_config nested keys also follow snake_case (action handler
    // reads `entity_type`, `task_template_id`, `data.listing_id`, etc.).
    action_config: a.actionConfig
      ? (deepRekeyCamelToSnake(a.actionConfig) as Record<string, unknown>)
      : a.actionConfig,
    condition: a.condition ?? null,
    on_failure: a.onFailure,
  }
}

function actionFromBackend(a: BackendAction & FrontendAction): FrontendAction {
  const rawCfg = a.actionConfig ?? a.action_config
  return {
    name: a.name,
    actionType: a.actionType ?? a.action_type,
    actionConfig: rawCfg
      ? (deepRekeySnakeToCamel(rawCfg) as Record<string, unknown>)
      : {},
    condition: a.condition ?? null,
    onFailure: a.onFailure ?? a.on_failure ?? 'abort',
  }
}

export function definitionToBackend(def: any): any {
  if (!def || typeof def !== 'object') return def
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(def)) {
    if (k === 'actions' && Array.isArray(v)) {
      out.actions = (v as FrontendAction[]).map(actionToBackend)
    } else if (k === 'dataDependencies' && Array.isArray(v)) {
      // Dependency item keys must be snake_case too — the executor reads
      // `dep.get("load_relationships")` (camelCase is only a tolerated
      // fallback we must not rely on). Deep-rekey converts loadRelationships
      // → load_relationships; key/entity/source are single words (unchanged),
      // and values (source paths, entity names) are untouched.
      out.data_dependencies = deepRekeyCamelToSnake(v)
    } else {
      out[k] = v
    }
  }
  return out
}

export function definitionFromBackend(def: any): any {
  if (!def || typeof def !== 'object') return def
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(def)) {
    if (k === 'actions' && Array.isArray(v)) {
      out.actions = (v as Array<BackendAction & FrontendAction>).map(actionFromBackend)
    } else if (k === 'data_dependencies' && Array.isArray(v)) {
      // Mirror of definitionToBackend: backend stores snake_case
      // load_relationships; the editor/types use camelCase loadRelationships.
      out.dataDependencies = deepRekeySnakeToCamel(v)
    } else {
      out[k] = v
    }
  }
  return out
}

export function workflowPayloadToBackend(params: any): any {
  if (!params || typeof params !== 'object') return params
  const out: Record<string, any> = { ...params }
  if (params.definition) out.definition = definitionToBackend(params.definition)
  // triggerConfig inner keys MUST be snake_case. The backend reads
  // trigger_config.get("event_pattern") (WorkflowRegistry.load) and
  // ("schedule_cron")/("schedule_timezone") (schedule-binding sync), and
  // `trigger_config` is a pass-through Dict[str, Any] — the inner keys we send
  // are persisted verbatim. The editor stores camelCase (eventPattern /
  // scheduleCron / scheduleTimezone), so WITHOUT this an event workflow saved
  // via the UI is stored as {eventPattern: ...} and is NEVER indexed by pattern
  // → registry.match never returns it → the workflow never fires.
  if (params.triggerConfig && typeof params.triggerConfig === 'object') {
    out.triggerConfig = deepRekeyCamelToSnake(params.triggerConfig)
  }
  // Activation impact confirmation flag. The backend reads it as snake_case
  // `confirm_materialization` on the request body (NOT a workflow-definition
  // field): activating a workflow that would create tasks without this flag
  // returns 409 MATERIALIZATION_CONFIRMATION_REQUIRED. Emit snake_case and
  // drop the camelCase key so it doesn't leak through as an unknown field.
  if ('confirmMaterialization' in out) {
    if (params.confirmMaterialization !== undefined) {
      out.confirm_materialization = params.confirmMaterialization
    }
    delete out.confirmMaterialization
  }
  return out
}

export function workflowResponseFromBackend(resp: any): any {
  if (!resp || typeof resp !== 'object') return resp
  const out: Record<string, any> = { ...resp }
  if (resp.definition) out.definition = definitionFromBackend(resp.definition)
  // Mirror the request side: backend stores snake_case trigger_config inner
  // keys; the editor (TriggerCard) reads camelCase (eventPattern / scheduleCron
  // / scheduleTimezone).
  if (resp.triggerConfig && typeof resp.triggerConfig === 'object') {
    out.triggerConfig = deepRekeySnakeToCamel(resp.triggerConfig)
  }
  return out
}
